import { useCurrentStateAndParams } from "@uirouter/react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { AIReportCtaPlacement, AIReportCtaSection } from "app/components/ai-reports-cta-section/AIReportCtaSection";
import EducationContainerProminentBig from "app/components/educationbar/educationContainerProminentBig";
import EngagementOverviewTable from "app/components/React/EngagementOverviewTable/EngagementOverviewTable";
import { MarketingChannelsDistribution } from "app/components/React/MarketingChannelsDistribution/MarketingChannelsDistribution";
import { WWOAllTrafficChannelsOverviewBarChartContainerDS } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/WWOAllTrafficChannelsOverviewBarChartContainerDS";
import { WWOTrafficEngagementWidgets } from "app/pages/website-analysis/TrafficAndEngagement/Components/Compare/WWOTrafficEngagementWidgets";
import { WWOSearchTrafficCompareContainer } from "app/pages/website-performance/components/SearchTraffic/compare/WWOSearchTrafficCompareContainer";
import { SectionHeader } from "app/pages/website-performance/components/SectionHeader";
import { WWOSocialOverviewContainerDS } from "app/pages/website-performance/components/SocialOverview/WWOSocialOverviewContainerDS";
import { WWOTopAdDestinationsContainerDS } from "app/pages/website-performance/components/TopAdDestinations/WWOTopAdDestinationsContainerDS";
import { WWOTrafficTopCountriesContainerDS } from "app/pages/website-performance/components/TopCountries/WWOTrafficTopCountriesContainerDS";
import { WWOTopPublishersTableContainerDS } from "app/pages/website-performance/components/TopPublishers/WWOTopPublishersTableContainerDS";
import { WWOTopReferringWebsitesContainerDS } from "app/pages/website-performance/components/TopReferringWebsites/WWOTopReferringWebsitesContainerDS";
import { WWOTrafficVisitsOverTimeContainerDS } from "app/pages/website-performance/components/TrafficVisitsOverTime/WWOTrafficVisitsOverTimeContainerDS";
import {
    AICtaSectionWrapper,
    EngagementOverviewTableWrapper,
    FullWidthWrapper,
    HeaderWithChannelWrapper,
    RowWrapper,
    SearchTrafficWrapper,
    SectionHeaderWrapper,
} from "app/pages/website-performance/StyledComponents";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

export const DigitalSuiteWorldwideOverviewCompare = () => {
    const { params } = useCurrentStateAndParams();
    const { translate } = useTranslate();
    const { webSource } = params;

    return (
        <>
            <EducationContainerProminentBig />
            <div className="row-fluid">
                <div className="wwo-widgets">
                    <RowWrapper className="row-flex">
                        <Text
                            margin="0 0 24px 0"
                            fontVariant="h4"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate("wa.wwo.section.webaudience.title")}
                        </Text>
                        <WWOTrafficEngagementWidgets />

                        <EngagementOverviewTableWrapper>
                            <EngagementOverviewTable showAddToDashboard={false} />
                        </EngagementOverviewTableWrapper>

                        <AICtaSectionWrapper>
                            <AIReportCtaSection placement={AIReportCtaPlacement.PERFORMANCE} />
                        </AICtaSectionWrapper>
                    </RowWrapper>
                    <RowWrapper className="row-flex pdf-page">
                        <FullWidthWrapper>
                            <WWOTrafficVisitsOverTimeContainerDS />
                        </FullWidthWrapper>
                        <SectionHeaderWrapper>
                            <SectionHeader sectionTitleKey="wa.wwo.section.geo.title" />
                        </SectionHeaderWrapper>
                        <FullWidthWrapper>
                            <WWOTrafficTopCountriesContainerDS />
                        </FullWidthWrapper>
                    </RowWrapper>

                    {webSource !== devicesTypes.MOBILE ? (
                        <>
                            <RowWrapper className="row-flex pdf-page">
                                <SectionHeaderWrapper>
                                    <SectionHeader sectionTitleKey="wa.wwo.section.marketingmix.title" />
                                </SectionHeaderWrapper>
                                <WWOAllTrafficChannelsOverviewBarChartContainerDS />
                            </RowWrapper>

                            <RowWrapper className="row-flex pdf-page">
                                <HeaderWithChannelWrapper>
                                    <SectionHeader sectionTitleKey="wwo.search.traffic.organic.section.title" />
                                    <MarketingChannelsDistribution marketingChannelsType={4} />
                                </HeaderWithChannelWrapper>
                                <SearchTrafficWrapper>
                                    <WWOSearchTrafficCompareContainer organic />
                                </SearchTrafficWrapper>

                                <HeaderWithChannelWrapper>
                                    <SectionHeader sectionTitleKey="wwo.search.traffic.paid.section.title" />
                                    <MarketingChannelsDistribution marketingChannelsType={5} />
                                </HeaderWithChannelWrapper>
                                <SearchTrafficWrapper>
                                    <WWOSearchTrafficCompareContainer organic={false} />
                                </SearchTrafficWrapper>
                            </RowWrapper>
                        </>
                    ) : null}

                    {webSource !== devicesTypes.MOBILE ? (
                        <>
                            <RowWrapper className="row-flex pdf-page">
                                <HeaderWithChannelWrapper>
                                    <SectionHeader sectionTitleKey="analysis.sources.referrals.title" />
                                    <MarketingChannelsDistribution marketingChannelsType={2} />
                                </HeaderWithChannelWrapper>
                                <FullWidthWrapper>
                                    <WWOTopReferringWebsitesContainerDS />
                                </FullWidthWrapper>

                                <SectionHeaderWrapper>
                                    <SectionHeader sectionTitleKey="wa.wwo.section.trafficdest.title" />
                                </SectionHeaderWrapper>
                                <FullWidthWrapper>
                                    <WWOTopAdDestinationsContainerDS />
                                </FullWidthWrapper>
                            </RowWrapper>

                            <RowWrapper className="row-flex pdf-page">
                                <HeaderWithChannelWrapper>
                                    <SectionHeader sectionTitleKey="analysis.sources.social.title" />
                                    <MarketingChannelsDistribution marketingChannelsType={3} />
                                </HeaderWithChannelWrapper>
                                <FullWidthWrapper>
                                    <WWOSocialOverviewContainerDS />
                                </FullWidthWrapper>
                            </RowWrapper>
                        </>
                    ) : null}
                    <RowWrapper className="row-flex pdf-page">
                        {webSource !== devicesTypes.MOBILE ? (
                            <HeaderWithChannelWrapper>
                                <SectionHeader sectionTitleKey="wa.wwo.section.displayads.title" />
                                <MarketingChannelsDistribution marketingChannelsType={6} />
                            </HeaderWithChannelWrapper>
                        ) : (
                            <SectionHeaderWrapper>
                                <SectionHeader sectionTitleKey="wa.wwo.section.displayads.title" />
                            </SectionHeaderWrapper>
                        )}
                        <FullWidthWrapper>
                            <WWOTopPublishersTableContainerDS />
                        </FullWidthWrapper>
                    </RowWrapper>
                </div>
            </div>
        </>
    );
};
